/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/hooks/useOnlineStatus.ts
 *
 * Rôle :
 *   Hook React qui expose l'état du réseau (online / offline) ET l'état
 *   du sync manager (en cours de synchronisation, nombre d'items en
 *   attente). Sert à piloter `<OfflineBanner>` et tout composant qui veut
 *   désactiver une action selon l'état réseau.
 *
 *   Note : `navigator.onLine` est connu pour mentir parfois (notamment
 *   captive portals). Le sync manager (§18) considère qu'une mutation
 *   échoue avec TypeError = vraiment hors ligne, ce qui est plus fiable.
 *   Ce hook combine les deux signaux pour une UX correcte.
 *
 * Réutilisable :
 *   100 %. Couplage avec syncManager.ts (qui est lui-même générique).
 *
 * @author Mika
 * @since 2026-05-04
 */

import { useEffect, useState, useSyncExternalStore } from 'react';
import { getSyncSnapshot, subscribeSyncState } from '@/offline/syncManager';

export interface OnlineStatus {
  /** Le navigateur croit qu'on est en ligne (`navigator.onLine`). */
  online: boolean;
  /** Le sync manager est en cours de drain. */
  draining: boolean;
  /** Nombre d'items dans la queue offline (estimé). */
  pendingCount: number;
}

/**
 * Hook de l'état réseau + sync.
 *
 * @example
 *   const { online, draining, pendingCount } = useOnlineStatus();
 *   if (!online) return <OfflineBanner pending={pendingCount} />;
 */
export function useOnlineStatus(): OnlineStatus {
  // Partie navigateur : online/offline events.
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Partie sync manager : useSyncExternalStore est l'API React 18 idiomatique
  // pour s'abonner à un store externe (notre syncManager).
  const sync = useSyncExternalStore(
    subscribeSyncState,
    getSyncSnapshot,
    getSyncSnapshot, // server snapshot (SSR-safe ; on ne fait pas de SSR mais bonne pratique)
  );

  return {
    online,
    draining: sync.draining,
    pendingCount: sync.pendingCount,
  };
}
